/**
 * ResultScreen.tsx - Results display for the Endowment experiment (Corporate Design)
 * Exports:
 *  - EndowmentResultScreen: ScreenConfig (for other code expecting ScreenConfig)
 *  - ResultScreen: React.FC (for sequence imports that expect a component)
 */

import React, { useEffect, useState, useMemo } from "react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, Legend, ResponsiveContainer, LabelList } from "recharts";
import { Switch } from "@blueprintjs/core";
import { useSession } from "../../../SessionContext";
import { ScreenConfig } from "../Sequences";
import { EndowmentEffect } from "../../../../translations/experiments/EndowmentEffect";
import { useLanguage } from "../../../../context/LanguageContext";
import { colors, commonStyles, chartColors } from "../../../../components/colors";

const resultMessageStyle: React.CSSProperties = {
    ...commonStyles.card,
    marginTop: 32,
    textAlign: "center",
    fontSize: 17,
    background: colors.backgroundLight,
    maxWidth: 760,
    marginLeft: "auto",
    marginRight: "auto",
};

const chartBlockStyle: React.CSSProperties = {
    display: "flex",
    flexDirection: "row",
    gap: 24,
    justifyContent: "space-between",
    alignItems: "flex-start",
    marginBottom: 8
};

const chartStyle: React.CSSProperties = {
    minWidth: 400,
    maxWidth: 980,
    flex: 1
};

const barColorBuyer = chartColors.primary[0];
const barColorSeller = chartColors.alternative[0];

const OnlyNonZeroLabel = (props: any) => {
    const { value, x, y, width, fill } = props;
    if (!value || value === 0) return null;
    return (
        <text
            x={x + (width ? width / 2 : 0)}
            y={y}
            dy={-8}
            fill={fill || "#222"}
            fontSize={13}
            textAnchor="middle"
            fontWeight="600"
            style={{ pointerEvents: "none" }}
        >
            {value}
        </text>
    );
};

const DEFAULT_MAX = 100;
const MIN_BIG_MAX = 200;

function makePriceBuckets(maxPrice: number) {
    const arr: { price: number; label: string; Buyers: number; Sellers: number }[] = [];
    for (let p = 0; p <= maxPrice; p++) {
        arr.push({ price: p, label: `${p} €`, Buyers: 0, Sellers: 0 });
    }
    return arr;
}

function detectObservedMax(users: any[]) {
    let observedMax = 0;
    users.forEach(user => {
        const data = user?.EndowmentEffect;
        if (!data) return;
        Object.values(data).forEach((caseObj: any) => {
            if (!caseObj || typeof caseObj !== "object") return;
            if (typeof caseObj.willingToPay === "number") observedMax = Math.max(observedMax, Math.trunc(caseObj.willingToPay));
            if (typeof caseObj.askedPrice === "number") observedMax = Math.max(observedMax, Math.trunc(caseObj.askedPrice));
            if (caseObj.selections && Array.isArray(caseObj.selections)) {
                caseObj.selections.forEach((sel: any) => {
                    if (typeof sel.willingToPay === "number") observedMax = Math.max(observedMax, Math.trunc(sel.willingToPay));
                    if (typeof sel.askedPrice === "number") observedMax = Math.max(observedMax, Math.trunc(sel.askedPrice));
                });
            }
        });
    });
    return observedMax;
}

function extractDistributions(users: any[], clampMax: number) {
    const hist: { [price: number]: { Buyers: number; Sellers: number } } = {};
    users.forEach(user => {
        const data = user?.EndowmentEffect;
        if (!data) return;
        Object.values(data).forEach((caseObj: any) => {
            if (!caseObj || typeof caseObj !== "object") return;

            if (typeof caseObj.willingToPay === "number") {
                const p = Math.trunc(caseObj.willingToPay);
                if (p >= 0 && p <= clampMax) {
                    if (!hist[p]) hist[p] = { Buyers: 0, Sellers: 0 };
                    hist[p].Buyers++;
                }
            }
            if (typeof caseObj.askedPrice === "number") {
                const p = Math.trunc(caseObj.askedPrice);
                if (p > 0 && p <= clampMax) {
                    if (!hist[p]) hist[p] = { Buyers: 0, Sellers: 0 };
                    hist[p].Sellers++;
                }
            }

            if (caseObj.selections && Array.isArray(caseObj.selections)) {
                caseObj.selections.forEach((sel: any) => {
                    if (typeof sel.willingToPay === "number") {
                        const p = Math.trunc(sel.willingToPay);
                        if (p >= 0 && p <= clampMax) {
                            if (!hist[p]) hist[p] = { Buyers: 0, Sellers: 0 };
                            hist[p].Buyers++;
                        }
                    }
                    if (typeof sel.askedPrice === "number") {
                        const p = Math.trunc(sel.askedPrice);
                        if (p > 0 && p <= clampMax) {
                            if (!hist[p]) hist[p] = { Buyers: 0, Sellers: 0 };
                            hist[p].Sellers++;
                        }
                    }
                });
            }
        });
    });
    return hist;
}

function getEndowmentInfoText(userData: any, t: any, language: string) {
    const selections: Array<{ role: "buyer" | "seller"; price: number; reason?: string }> = [];
    const ee = userData?.EndowmentEffect;
    if (!ee) return t.yourChoicesNone || (language === "de" ? "Du hast noch keine Entscheidung getroffen." : "You have not made a choice yet.");

    Object.values(ee).forEach((caseObj: any) => {
        if (!caseObj || typeof caseObj !== "object") return;
        if (typeof caseObj.willingToPay === "number") selections.push({ role: "buyer", price: Math.trunc(caseObj.willingToPay), reason: caseObj.reason });
        if (typeof caseObj.askedPrice === "number") selections.push({ role: "seller", price: Math.trunc(caseObj.askedPrice), reason: caseObj.reason });
        if (caseObj.selections && Array.isArray(caseObj.selections)) {
            caseObj.selections.forEach((sel: any) => {
                if (typeof sel.willingToPay === "number") selections.push({ role: "buyer", price: Math.trunc(sel.willingToPay), reason: sel.reason || caseObj.reason });
                if (typeof sel.askedPrice === "number") selections.push({ role: "seller", price: Math.trunc(sel.askedPrice), reason: sel.reason || caseObj.reason });
            });
        }
    });

    if (selections.length === 0) {
        return t.yourChoicesNone || (language === "de" ? "Du hast noch keine Entscheidung getroffen." : "You have not made a choice yet.");
    }

    if (typeof t.yourChoicesSome === "function") {
        return (
            <span dangerouslySetInnerHTML={{
                __html: t.yourChoicesSome(selections.map(s => ({ role: s.role, price: s.price, reason: s.reason || "" })))
            }} />
        );
    }

    return (
        <div style={{ textAlign: "left", fontSize: 15 }}>
            {selections.map((s, i) => (
                <div key={i} style={{ marginBottom: 10 }}>
                    <div>
                        {language === "de"
                            ? s.role === "seller"
                                ? <><b>Als Verkäufer:</b> Du wolltest die Karte für <b>{s.price} €</b> verkaufen.</>
                                : <><b>Als Käufer:</b> Du wärst bereit, <b>{s.price} €</b> zu zahlen.</>
                            : s.role === "seller"
                                ? <><b>As seller:</b> You wanted to sell the ticket for <b>€{s.price}</b>.</>
                                : <><b>As buyer:</b> You would be willing to pay <b>€{s.price}</b>.</>
                        }
                    </div>
                    {s.reason ? <div style={{ color: colors.textMedium, marginTop: 6 }}>{language === "de" ? "Begründung:" : "Reason:"} {s.reason}</div> : null}
                </div>
            ))}
        </div>
    );
}

const CustomTooltip = ({ active, payload, label, total }: any) => {
    if (active && payload && payload.length) {
        const value = payload[0].value;
        const percentage = total > 0 ? Math.round((value / total) * 100) : 0;
        const labelText = typeof label === "number" ? `${label} €` : label;
        return (
            <div style={{
                background: "#fff",
                border: `1.5px solid ${colors.silver}`,
                padding: 12,
                borderRadius: 8,
                fontSize: 14,
                boxShadow: "0 2px 8px rgba(0,0,0,0.06)"
            }}>
                <div style={{ fontWeight: 600, marginBottom: 6, color: colors.brillantBlue }}>{labelText}</div>
                <div style={{ color: payload[0].color || colors.textMedium }}>{value} ({percentage}%)</div>
            </div>
        );
    }
    return null;
};

const switchCardStyle: React.CSSProperties = {
    background: "rgba(255,255,255,0.7)",
    boxShadow: "0 2px 12px rgba(0,0,0,0.07)",
    borderRadius: 10,
    border: "1px solid #e1e6ea",
    padding: "12px 22px",
    margin: "20px auto 18px auto",
    display: "flex",
    justifyContent: "center",
    gap: 24,
    width: "fit-content"
};

const EndowmentResultScreenComponent: React.FC = () => {
    const { getGroupData, userData } = useSession();
    const { language } = useLanguage();
    const t = EndowmentEffect[language].screens.result;
    const [allUsers, setAllUsers] = useState<any[]>([]);
    const [showBuyers, setShowBuyers] = useState(true);
    const [showSellers, setShowSellers] = useState(true);
    const [, setLoading] = useState(true);

    useEffect(() => {
        setLoading(true);
        getGroupData().then(data => {
            setAllUsers(data || []);
            setLoading(false);
        }).catch(() => setLoading(false));
        // eslint-disable-next-line
    }, []);

    const observedMax = useMemo(() => detectObservedMax(allUsers), [allUsers]);
    const displayMax = observedMax >= 100 ? Math.max(Math.ceil(observedMax / 10) * 10, MIN_BIG_MAX) : DEFAULT_MAX;
    const histogram = useMemo(() => extractDistributions(allUsers, displayMax), [allUsers, displayMax]);

    const chartData = useMemo(() => makePriceBuckets(displayMax).map(item => ({
        price: item.price,
        label: item.label,
        Buyers: showBuyers ? (histogram[item.price]?.Buyers || 0) : 0,
        Sellers: showSellers ? (histogram[item.price]?.Sellers || 0) : 0
    })), [histogram, showBuyers, showSellers, displayMax]);

    const ticks = useMemo(() => {
        const step = 10;
        const values: number[] = [];
        for (let p = 0; p <= displayMax; p += step) values.push(p);
        return values;
    }, [displayMax]);

    const totalBars = useMemo(() => chartData.reduce((sum, d) => sum + (d.Buyers || 0) + (d.Sellers || 0), 0), [chartData]);
    const infoText = getEndowmentInfoText(userData, t, language);

    return (
        <div className="experiment-container" style={commonStyles.container}>
            <h2 style={{
                ...commonStyles.heading,
                margin: "0 0 24px 0",
                fontSize: 28,
                fontWeight: 700
            }}>
                {t.title}
            </h2>

            <div style={{
                fontSize: 16.5,
                color: colors.textMedium,
                fontStyle: "italic",
                marginBottom: 14,
                textAlign: "center"
            }}>
            </div>

            <div style={switchCardStyle}>
                <Switch
                    checked={showBuyers}
                    label={language === "de" ? "Käufer:innen" : "Buyers"}
                    onChange={() => setShowBuyers(v => !v)}
                    style={{ fontWeight: 500, fontSize: 15 }}
                />
                <Switch
                    checked={showSellers}
                    label={language === "de" ? "Verkäufer:innen" : "Sellers"}
                    onChange={() => setShowSellers(v => !v)}
                    style={{ fontWeight: 500, fontSize: 15 }}
                />
            </div>

            <div style={{ ...chartBlockStyle, marginBottom: 8 }}>
                <div style={{ ...chartStyle, margin: "0 auto" }}>
                    <ResponsiveContainer width="100%" height={420}>
                        <BarChart data={chartData} barCategoryGap="6%">
                            <XAxis
                                dataKey="price"
                                tickFormatter={(v: number) => `${v} €`}
                                ticks={ticks}
                                tick={{ fontSize: 12, angle: -45, textAnchor: "end" } as any}
                                height={80}
                            />
                            <YAxis allowDecimals={false} />
                            <Tooltip content={<CustomTooltip total={totalBars} />} cursor={{ fill: 'rgba(200, 200, 200, 0.18)' }} />
                            <Legend wrapperStyle={{ fontSize: 15, fontWeight: 500, marginBottom: 8 }} />
                            {showBuyers && (
                                <Bar dataKey="Buyers" fill={barColorBuyer} maxBarSize={20} radius={[3, 3, 0, 0]} name={language === "de" ? "Käufer:innen" : "Buyers"}>
                                    <LabelList dataKey="Buyers" content={OnlyNonZeroLabel} />
                                </Bar>
                            )}
                            {showSellers && (
                                <Bar dataKey="Sellers" fill={barColorSeller} maxBarSize={20} radius={[3, 3, 0, 0]} name={language === "de" ? "Verkäufer:innen" : "Sellers"}>
                                    <LabelList dataKey="Sellers" content={OnlyNonZeroLabel} />
                                </Bar>
                            )}
                        </BarChart>
                    </ResponsiveContainer>
                </div>
            </div>

            <div style={resultMessageStyle}>
                {infoText}
            </div>
        </div>
    );
};

export const EndowmentResultScreen: ScreenConfig = {
    component: EndowmentResultScreenComponent,
    defaultResult: {},
    canContinueDefault: true,
};

// Provide a React component named ResultScreen for imports that expect a component
export const ResultScreen: React.FC<any> = (props: any) => <EndowmentResultScreenComponent {...props} />;